import { useContext } from 'react';
import { LayoutContext } from '@/_shared/components/PageLayout/LayoutContext';

/**
 * Hook to inject or remove a banner in the page layout.
 *
 * The banner is rendered between the Header and the main content area,
 * making it visible across all pages that use `PageLayout`.
 *
 * Multiple banners can coexist, each identified by a unique key.
 * Setting the same key again replaces the previous banner of that type.
 *
 * @returns {object} `{ banners, setBanner }`
 *   - `banners` — record of active banners keyed by type
 *   - `setBanner(key, node)` — set a banner by key; pass `null` to remove it
 *
 * @example
 * const { setBanner } = useLayoutBanner();
 *
 * useEffect(() => {
 *   setBanner('trialExpiring', <Banner message='Hello' type='info' />);
 *   return () => setBanner('trialExpiring', null);
 * }, [setBanner]);
 */
export const useLayoutBanner = () => {
  const { banners, setBanner } = useContext(LayoutContext);
  return { banners, setBanner };
};
